import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Copy, Upload, Edit2, Trash2, BarChart3, FileText, LogOut, ChevronLeft, ChevronRight, Lock, Unlock, Calendar, X, Download, CheckCircle2, AlertCircle, Users, DollarSign, ArrowLeft, Bell } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RESULTS_PER_PAGE = 20;

export default function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('results');
  const [results, setResults] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedQuiz, setSelectedQuiz] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [questionCounts, setQuestionCounts] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Date filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Download report state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadStartDate, setDownloadStartDate] = useState('');
  const [downloadEndDate, setDownloadEndDate] = useState('');

  // CSV files state
  const [quizCSV, setQuizCSV] = useState(null);
  const [questionsCSV, setQuestionsCSV] = useState(null);
  const [answersCSV, setAnswersCSV] = useState(null);
  const [resultsCSV, setResultsCSV] = useState(null);
  const [traitsCSV, setTraitsCSV] = useState(null);

  // Drag-and-drop state
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [detectionResults, setDetectionResults] = useState({});

  // Affiliates state
  const [affiliates, setAffiliates] = useState([]);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [affiliateReferrals, setAffiliateReferrals] = useState([]);
  const [affiliateReferralsLoading, setAffiliateReferralsLoading] = useState(false);
  const [editingAffiliateBalance, setEditingAffiliateBalance] = useState(null);
  const [newBalanceValue, setNewBalanceValue] = useState('');
  const [payoutModalAffiliate, setPayoutModalAffiliate] = useState(null);

  const CSV_FIELD_SIGNATURES = {
    quiz: { keywords: ['quiz_id', 'quiz_name', 'quiz_description', 'quiz_price'], setter: setQuizCSV, label: 'Quiz CSV' },
    questions: { keywords: ['question_id', 'question_text'], setter: setQuestionsCSV, label: 'Questions CSV' },
    answers: { keywords: ['answer_id', 'answer_text', 'answer_result'], setter: setAnswersCSV, label: 'Answers CSV' },
    results: { keywords: ['result_id', 'result_name', 'result_text', 'result_image'], setter: setResultsCSV, label: 'Results CSV' },
    traits: { keywords: ['trait_id', 'trait_name', 'trait_text', 'trait_locked'], setter: setTraitsCSV, label: 'Traits CSV' },
  };

  const detectCSVType = useCallback((file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const header = e.target.result.split('\n')[0].toLowerCase().replace(/\s/g, '');
        let bestMatch = null;
        let bestScore = 0;
        for (const [type, config] of Object.entries(CSV_FIELD_SIGNATURES)) {
          const score = config.keywords.filter(k => header.includes(k)).length;
          if (score > bestScore) { bestScore = score; bestMatch = type; }
        }
        resolve(bestScore > 0 ? bestMatch : null);
      };
      reader.readAsText(file.slice(0, 512));
    });
  }, []);

  const handleDroppedFiles = useCallback(async (files) => {
    const results = {};
    for (const file of Array.from(files)) {
      if (!file.name.endsWith('.csv')) {
        results[file.name] = { status: 'error', message: 'Not a CSV file' };
        continue;
      }
      const type = await detectCSVType(file);
      if (type) {
        CSV_FIELD_SIGNATURES[type].setter(file);
        results[file.name] = { status: 'success', message: `Detected as ${CSV_FIELD_SIGNATURES[type].label}` };
      } else {
        results[file.name] = { status: 'error', message: 'Could not detect CSV type' };
      }
    }
    setDetectionResults(results);
  }, [detectCSVType]);

  // Lock/Unlock modal state
  const [showLockModal, setShowLockModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchResults();
    fetchQuizzes();
  }, []);

  useEffect(() => {
    if (activeTab === 'statistics') {
      fetchStats();
    }
    if (activeTab === 'affiliates') {
      fetchAffiliates();
    }
  }, [activeTab, selectedQuiz]);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/results`);
      setResults(response.data);
    } catch (error) {
      console.error('Error fetching results:', error);
    }
  };

  const fetchQuizzes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/quizzes`);
      const quizzesData = response.data;
      setQuizzes(quizzesData);

      // Fetch question counts for each quiz
      const counts = {};
      await Promise.all(
        quizzesData.map(async (quiz) => {
          try {
            const qRes = await axios.get(`${BACKEND_URL}/api/quizzes/${quiz.quiz_id}/questions`);
            counts[quiz.quiz_id] = qRes.data.length;
          } catch {
            counts[quiz.quiz_id] = 0;
          }
        })
      );
      setQuestionCounts(counts);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const url = selectedQuiz
        ? `${BACKEND_URL}/api/admin/stats?quiz_id=${selectedQuiz}`
        : `${BACKEND_URL}/api/admin/stats`;
      const response = await axios.get(url);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCopyUID = (uid) => {
    const url = `${window.location.origin}/results/${uid}`;
    navigator.clipboard.writeText(url);
    toast.success(t('admin.copied'));
  };

  const handleToggleLock = (result) => {
    setSelectedResult(result);
    setShowLockModal(true);
  };

  const confirmToggleLock = async () => {
    if (!selectedResult) return;

    try {
      const newStatus = !selectedResult.results_unlocked;
      
      await axios.put(`${BACKEND_URL}/api/admin/results/${selectedResult.uid}/toggle-lock`, {
        results_unlocked: newStatus
      });

      toast.success(newStatus ? 'Result unlocked successfully' : 'Result locked successfully');
      fetchResults();
      setShowLockModal(false);
      setSelectedResult(null);
    } catch (error) {
      console.error('Error toggling lock:', error);
      toast.error('Failed to update result status');
    }
  };

  const handleDownloadReport = () => {
    // Filter results by date range
    let filteredResults = results;
    
    if (downloadStartDate || downloadEndDate) {
      filteredResults = results.filter((result) => {
        const resultDate = new Date(result.created_at);
        
        if (downloadStartDate && downloadEndDate) {
          const start = new Date(downloadStartDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(downloadEndDate);
          end.setHours(23, 59, 59, 999);
          return resultDate >= start && resultDate <= end;
        } else if (downloadStartDate) {
          const start = new Date(downloadStartDate);
          start.setHours(0, 0, 0, 0);
          return resultDate >= start;
        } else if (downloadEndDate) {
          const end = new Date(downloadEndDate);
          end.setHours(23, 59, 59, 999);
          return resultDate <= end;
        }
        
        return true;
      });
    }

    if (filteredResults.length === 0) {
      toast.error('No results found for the selected date range');
      return;
    }

    // Create CSV content
    const headers = [
      'UID',
      'Quiz Name',
      'Result Name',
      'Email',
      'Country',
      'Currency',
      'Payment Status',
      'Results Unlocked',
      'Payment Reference',
      'IP Address',
      'City',
      'Postal Code',
      'Created Date'
    ];

    const csvRows = [headers.join(',')];

    filteredResults.forEach((result) => {
      const row = [
        result.uid,
        `"${result.quiz_name}"`,
        `"${result.result_name}"`,
        result.user_email || '',
        result.country_name || '',
        result.currency,
        result.payment_status,
        result.results_unlocked ? 'Yes' : 'No',
        result.payment_reference || '',
        result.ip_address || '',
        result.city || '',
        result.postal_code || '',
        format(new Date(result.created_at), 'yyyy-MM-dd HH:mm:ss')
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const fileName = downloadStartDate && downloadEndDate
      ? `quiz_results_${downloadStartDate}_to_${downloadEndDate}.csv`
      : downloadStartDate
      ? `quiz_results_from_${downloadStartDate}.csv`
      : downloadEndDate
      ? `quiz_results_until_${downloadEndDate}.csv`
      : `quiz_results_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Downloaded ${filteredResults.length} results`);
    setShowDownloadModal(false);
    setDownloadStartDate('');
    setDownloadEndDate('');
  };

  const handleUploadCSV = async () => {
    const formData = new FormData();
    if (quizCSV) formData.append('quiz_csv', quizCSV);
    if (questionsCSV) formData.append('questions_csv', questionsCSV);
    if (answersCSV) formData.append('answers_csv', answersCSV);
    if (resultsCSV) formData.append('results_csv', resultsCSV);
    if (traitsCSV) formData.append('traits_csv', traitsCSV);

    try {
      await axios.post(`${BACKEND_URL}/api/admin/upload-csv`, formData);
      toast.success('Quiz uploaded successfully');
      fetchQuizzes();
      // Reset files
      setQuizCSV(null);
      setQuestionsCSV(null);
      setAnswersCSV(null);
      setResultsCSV(null);
      setTraitsCSV(null);
      setDetectionResults({});
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast.error('Failed to upload quiz');
    }
  };

  const handleUpdateQuiz = async (quizId, data) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/quizzes/${quizId}`, data);
      toast.success('Quiz updated successfully');
      fetchQuizzes();
      setEditingQuiz(null);
    } catch (error) {
      console.error('Error updating quiz:', error);
      toast.error('Failed to update quiz');
    }
  };

  const handleDeleteQuiz = async (quizId) => {
    if (!window.confirm('Are you sure you want to delete this quiz?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/quizzes/${quizId}`);
      toast.success('Quiz deleted successfully');
      fetchQuizzes();
    } catch (error) {
      console.error('Error deleting quiz:', error);
      toast.error('Failed to delete quiz');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/joseph');
  };

  const fetchAffiliates = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/affiliates`);
      setAffiliates(res.data);
    } catch (err) {
      console.error('Error fetching affiliates:', err);
    }
  };

  const handleSelectAffiliate = async (aff) => {
    setSelectedAffiliate(aff);
    setAffiliateReferralsLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/affiliates/${aff.affiliate_id}/referrals`);
      setAffiliateReferrals(res.data);
    } catch (err) {
      console.error('Error fetching affiliate referrals:', err);
    } finally {
      setAffiliateReferralsLoading(false);
    }
  };

  const handleUpdateAffiliateBalance = async (affiliateId) => {
    const val = parseFloat(newBalanceValue);
    if (isNaN(val) || val < 0) { toast.error('Enter a valid balance'); return; }
    try {
      await axios.put(`${BACKEND_URL}/api/admin/affiliates/${affiliateId}/balance`, { balance: val });
      toast.success('Balance updated');
      setEditingAffiliateBalance(null);
      setNewBalanceValue('');
      fetchAffiliates();
      if (selectedAffiliate?.affiliate_id === affiliateId) {
        setSelectedAffiliate(prev => ({ ...prev, balance: val }));
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to update balance');
    }
  };

  const handleDeleteAffiliate = async (affiliateId) => {
    if (!window.confirm('Are you sure you want to delete this affiliate?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/affiliates/${affiliateId}`);
      toast.success('Affiliate deleted');
      if (selectedAffiliate?.affiliate_id === affiliateId) setSelectedAffiliate(null);
      fetchAffiliates();
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete affiliate');
    }
  };

  const handleMarkPayoutSeen = async (affiliateId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/affiliates/${affiliateId}/mark-payout-seen`);
      toast.success('Payout request marked as reviewed');
      setPayoutModalAffiliate(null);
      fetchAffiliates();
    } catch (err) {
      console.error(err);
      toast.error('Failed to mark as seen');
    }
  };

  const handleClearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  const handleApplyDateFilter = () => {
    setShowDatePicker(false);
    setCurrentPage(1);
    if (startDate || endDate) {
      toast.success('Date filter applied');
    }
  };

  // Filter results by date range
  const dateFilteredResults = results.filter((result) => {
    const resultDate = new Date(result.created_at);
    
    if (startDate && endDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      return resultDate >= start && resultDate <= end;
    } else if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      return resultDate >= start;
    } else if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      return resultDate <= end;
    }
    
    return true;
  });

  const sortedResults = [...dateFilteredResults].sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.created_at) - new Date(a.created_at);
    } else if (sortBy === 'currency') {
      return a.currency.localeCompare(b.currency);
    } else if (sortBy === 'country') {
      return (a.country_name || '').localeCompare(b.country_name || '');
    }
    return 0;
  });

  // Pagination calculations
  const totalPages = Math.ceil(sortedResults.length / RESULTS_PER_PAGE);
  const startIndex = (currentPage - 1) * RESULTS_PER_PAGE;
  const endIndex = startIndex + RESULTS_PER_PAGE;
  const paginatedResults = sortedResults.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-center gap-2 mt-6">
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-2 rounded-lg bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => goToPage(1)}
              className="px-4 py-2 rounded-lg bg-white text-slate-700 hover:bg-slate-100 transition-colors"
            >
              1
            </button>
            {startPage > 2 && <span className="text-slate-400">...</span>}
          </>
        )}

        {pages.map((page) => (
          <button
            key={page}
            onClick={() => goToPage(page)}
            className={`px-4 py-2 rounded-lg transition-colors ${
              currentPage === page
                ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-bold'
                : 'bg-white text-slate-700 hover:bg-slate-100'
            }`}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="text-slate-400">...</span>}
            <button
              onClick={() => goToPage(totalPages)}
              className="px-4 py-2 rounded-lg bg-white text-slate-700 hover:bg-slate-100 transition-colors"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-100 via-fuchsia-50 to-sky-100">
      {/* Header */}
      <header className="bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-slate-900">{t('admin.dashboard')}</h1>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('results')}
            className={`px-6 py-3 rounded-full font-bold transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'results'
                ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg'
                : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="admin-results-tab"
          >
            <FileText className="w-4 h-4" />
            {t('admin.results')}
          </button>
          <button
            onClick={() => setActiveTab('statistics')}
            className={`px-6 py-3 rounded-full font-bold transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'statistics'
                ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg'
                : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="admin-statistics-tab"
          >
            <BarChart3 className="w-4 h-4" />
            {t('admin.statistics')}
          </button>
          <button
            onClick={() => setActiveTab('quizManagement')}
            className={`px-6 py-3 rounded-full font-bold transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'quizManagement'
                ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg'
                : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="admin-quiz-management-tab"
          >
            <Upload className="w-4 h-4" />
            {t('admin.quizManagement')}
          </button>
          <button
            onClick={() => { setActiveTab('affiliates'); setSelectedAffiliate(null); }}
            className={`relative px-6 py-3 rounded-full font-bold transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'affiliates'
                ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg'
                : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
            data-testid="admin-affiliates-tab"
          >
            <Users className="w-4 h-4" />
            Affiliates
            {affiliates.some(a => a.pending_payout) && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
            )}
          </button>
        </div>

        {/* Results Tab */}
        {activeTab === 'results' && (
          <div className="bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-lg">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4">
              <div className="flex flex-wrap items-center gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mr-2">Sort by:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 rounded-lg border-slate-200 bg-slate-50 focus:ring-2 focus:ring-violet-500/20"
                    data-testid="admin-sort-select"
                  >
                    <option value="date">Date</option>
                    <option value="currency">Currency</option>
                    <option value="country">Country</option>
                  </select>
                </div>

                {/* Date Filter Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowDatePicker(!showDatePicker)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      startDate || endDate
                        ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-md'
                        : 'bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200'
                    }`}
                    data-testid="date-filter-button"
                  >
                    <Calendar className="w-4 h-4" />
                    {startDate || endDate ? 'Date Filtered' : 'Filter by Date'}
                  </button>

                  {/* Date Picker Dropdown */}
                  {showDatePicker && (
                    <div className="absolute top-full mt-2 left-0 bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 z-50 min-w-[320px]">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-slate-900">Select Date Range</h3>
                        <button
                          onClick={() => setShowDatePicker(false)}
                          className="p-1 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <X className="w-4 h-4 text-slate-500" />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-4 py-2 rounded-lg border-slate-200 bg-slate-50 focus:ring-2 focus:ring-violet-500/20"
                            data-testid="start-date-input"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            End Date
                          </label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-4 py-2 rounded-lg border-slate-200 bg-slate-50 focus:ring-2 focus:ring-violet-500/20"
                            data-testid="end-date-input"
                          />
                        </div>

                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={handleClearDateFilter}
                            className="flex-1 px-4 py-2 rounded-lg font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors"
                            data-testid="clear-date-filter"
                          >
                            Clear
                          </button>
                          <button
                            onClick={handleApplyDateFilter}
                            className="flex-1 px-4 py-2 rounded-lg font-medium text-white bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:shadow-lg transition-all"
                            data-testid="apply-date-filter"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Download Report Button */}
                <button
                  onClick={() => setShowDownloadModal(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-emerald-500 text-white hover:bg-emerald-600 shadow-md transition-colors"
                  data-testid="download-report-button"
                >
                  <Download className="w-4 h-4" />
                  Download Report
                </button>

                {/* Active Date Filter Display */}
                {(startDate || endDate) && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-violet-50 rounded-lg border border-violet-200">
                    <span className="text-sm text-violet-700 font-medium">
                      {startDate && endDate
                        ? `${format(new Date(startDate), 'MMM dd, yyyy')} - ${format(new Date(endDate), 'MMM dd, yyyy')}`
                        : startDate
                        ? `From ${format(new Date(startDate), 'MMM dd, yyyy')}`
                        : `Until ${format(new Date(endDate), 'MMM dd, yyyy')}`}
                    </span>
                    <button
                      onClick={handleClearDateFilter}
                      className="p-1 hover:bg-violet-100 rounded transition-colors"
                      title="Clear date filter"
                    >
                      <X className="w-3 h-3 text-violet-600" />
                    </button>
                  </div>
                )}
              </div>

              <div className="text-sm text-slate-600">
                Showing {startIndex + 1}-{Math.min(endIndex, sortedResults.length)} of {sortedResults.length} results
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900">{t('admin.uid')}</th>
                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900">{t('admin.quizName')}</th>
                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900">{t('admin.resultName')}</th>
                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900">{t('admin.email')}</th>
                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900">{t('admin.country')}</th>
                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900">{t('admin.currency')}</th>
                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900">{t('admin.paymentStatus')}</th>
                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900">{t('admin.createdDate')}</th>
                    <th className="text-left py-3 px-4 text-sm font-bold text-slate-900">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedResults.map((result) => (
                    <tr key={result.uid} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleCopyUID(result.uid)}
                          className="flex items-center gap-2 text-violet-600 hover:text-violet-700 font-mono text-xs"
                          data-testid={`copy-uid-${result.uid}`}
                        >
                          <Copy className="w-3 h-3" />
                          {result.uid.slice(0, 8)}...
                        </button>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700">{result.quiz_name}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{result.result_name}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{result.user_email || '-'}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{result.country_name || '-'}</td>
                      <td className="py-3 px-4 text-sm text-slate-700">{result.currency}</td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            result.payment_status === 'paid'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {result.payment_status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-700">
                        {format(new Date(result.created_at), 'dd/MM/yy HH:mm')}
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => handleToggleLock(result)}
                          className={`p-2 rounded-lg transition-colors ${
                            result.results_unlocked
                              ? 'bg-red-100 text-red-600 hover:bg-red-200'
                              : 'bg-green-100 text-green-600 hover:bg-green-200'
                          }`}
                          data-testid={`toggle-lock-${result.uid}`}
                          title={result.results_unlocked ? 'Lock Result' : 'Unlock Result'}
                        >
                          {result.results_unlocked ? (
                            <Lock className="w-4 h-4" />
                          ) : (
                            <Unlock className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && renderPagination()}
          </div>
        )}

        {/* Statistics Tab */}
        {activeTab === 'statistics' && (
          <div className="space-y-6">
            <div className="bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-lg">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <div>
                  <p className="text-sm text-slate-600 mb-1">{t('admin.todayEarnings')}</p>
                  <p className="text-4xl font-bold text-slate-900">
                    ${stats?.today_earnings?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mr-2">{t('admin.selectQuiz')}:</label>
                  <select
                    value={selectedQuiz}
                    onChange={(e) => setSelectedQuiz(e.target.value)}
                    className="px-4 py-2 rounded-lg border-slate-200 bg-slate-50 focus:ring-2 focus:ring-violet-500/20"
                    data-testid="admin-stats-quiz-select"
                  >
                    <option value="">{t('admin.allQuizzes')}</option>
                    {quizzes.map((quiz) => (
                      <option key={quiz.quiz_id} value={quiz.quiz_id}>
                        {quiz.quiz_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {stats && (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={stats.daily_stats}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" stroke="#64748b" />
                    <YAxis stroke="#64748b" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="tests_taken"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      name={t('admin.testsTaken')}
                    />
                    <Line
                      type="monotone"
                      dataKey="tests_unlocked"
                      stroke="#ec4899"
                      strokeWidth={2}
                      name={t('admin.testsUnlocked')}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        )}

        {/* Quiz Management Tab */}
        {activeTab === 'quizManagement' && (
          <div className="space-y-8">
            {/* CSV Upload */}
            <div className="bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-lg">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('admin.uploadCSV')}</h2>
              <p className="text-sm text-slate-500 mb-6">Drop all your CSV files at once — they'll be auto-detected and assigned to the right fields.</p>

              {/* Drop Zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
                onDragLeave={() => setIsDraggingOver(false)}
                onDrop={(e) => { e.preventDefault(); setIsDraggingOver(false); handleDroppedFiles(e.dataTransfer.files); }}
                onClick={() => document.getElementById('bulk-csv-input').click()}
                className={`relative border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all duration-200 mb-6 ${
                  isDraggingOver
                    ? 'border-violet-500 bg-violet-50 scale-[1.01]'
                    : 'border-slate-300 bg-slate-50 hover:border-violet-400 hover:bg-violet-50/50'
                }`}
              >
                <input
                  id="bulk-csv-input"
                  type="file"
                  accept=".csv"
                  multiple
                  className="hidden"
                  onChange={(e) => handleDroppedFiles(e.target.files)}
                />
                <div className={`mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-colors ${isDraggingOver ? 'bg-violet-500' : 'bg-violet-100'}`}>
                  <Upload className={`w-7 h-7 ${isDraggingOver ? 'text-white' : 'text-violet-500'}`} />
                </div>
                <p className="text-lg font-bold text-slate-700 mb-1">
                  {isDraggingOver ? 'Release to upload' : 'Drag & drop CSV files here'}
                </p>
                <p className="text-sm text-slate-500">or click to browse — drop multiple files at once</p>
              </div>

              {/* Detection Results */}
              {Object.keys(detectionResults).length > 0 && (
                <div className="mb-6 space-y-2">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Detection Results</p>
                  {Object.entries(detectionResults).map(([filename, result]) => (
                    <div key={filename} className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm ${result.status === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                      {result.status === 'success'
                        ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                        : <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />}
                      <span className="font-mono text-xs text-slate-600 truncate flex-1">{filename}</span>
                      <span className={`font-medium shrink-0 ${result.status === 'success' ? 'text-green-700' : 'text-red-600'}`}>{result.message}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Assigned Files Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                {[
                  { key: 'quiz', label: 'Quiz', format: 'quiz_id, quiz_name, quiz_description, quiz_image, quiz_price', file: quizCSV, onRemove: () => setQuizCSV(null) },
                  { key: 'questions', label: 'Questions', format: 'question_id, question_text', file: questionsCSV, onRemove: () => setQuestionsCSV(null) },
                  { key: 'answers', label: 'Answers', format: 'answer_id, question_id, answer_text, answer_result', file: answersCSV, onRemove: () => setAnswersCSV(null) },
                  { key: 'results', label: 'Results', format: 'result_id, result_name, result_text, result_image', file: resultsCSV, onRemove: () => setResultsCSV(null) },
                  { key: 'traits', label: 'Traits', format: 'trait_id, result_id, trait_name, trait_text, trait_image, trait_locked', file: traitsCSV, onRemove: () => setTraitsCSV(null) },
                ].map(({ label, format: fmt, file, onRemove }) => (
                  <div key={label} className={`rounded-xl border p-3 transition-all ${file ? 'border-violet-300 bg-violet-50' : 'border-slate-200 bg-white'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-bold text-slate-700">{label}</span>
                      {file && (
                        <button onClick={onRemove} className="p-0.5 rounded hover:bg-violet-200 transition-colors">
                          <X className="w-3 h-3 text-violet-500" />
                        </button>
                      )}
                    </div>
                    {file ? (
                      <p className="text-xs text-violet-700 font-medium truncate">{file.name}</p>
                    ) : (
                      <p className="text-xs text-slate-400 italic truncate">{fmt}</p>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={handleUploadCSV}
                disabled={!quizCSV && !questionsCSV && !answersCSV && !resultsCSV && !traitsCSV}
                className="w-full h-12 rounded-full font-bold text-white shadow-lg transition-all hover:scale-105 bg-gradient-to-r from-violet-500 to-fuchsia-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                data-testid="upload-csv-button"
              >
                {t('admin.upload')}
              </button>
            </div>

            {/* Quiz List */}
            <div className="bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-lg">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">{t('admin.quizList')}</h2>
              
              <div className="space-y-4">
                {quizzes.map((quiz) => (
                  <div
                    key={quiz.quiz_id}
                    className="border border-slate-200 rounded-2xl p-4 hover:border-violet-300 transition-colors"
                  >
                    {editingQuiz === quiz.quiz_id ? (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">Quiz Name</label>
                          <input
                            type="text"
                            defaultValue={quiz.quiz_name}
                            id={`name-${quiz.quiz_id}`}
                            className="w-full px-4 py-2 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                            placeholder="Quiz Name"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">Description</label>
                          <textarea
                            defaultValue={quiz.quiz_description}
                            id={`description-${quiz.quiz_id}`}
                            rows={3}
                            className="w-full px-4 py-2 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-500/20 resize-none"
                            placeholder="Quiz Description"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">Image URL</label>
                          <input
                            type="text"
                            defaultValue={quiz.quiz_image}
                            id={`image-${quiz.quiz_id}`}
                            className="w-full px-4 py-2 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                            placeholder="Image URL"
                          />
                        </div>
                        <div className="flex gap-3">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-slate-500 mb-1">Price</label>
                            <input
                              type="number"
                              step="0.01"
                              defaultValue={quiz.quiz_price}
                              id={`price-${quiz.quiz_id}`}
                              className="w-full px-4 py-2 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                              placeholder="Price"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-slate-500 mb-1">Language Code</label>
                            <input
                              type="text"
                              defaultValue={quiz.language}
                              id={`language-${quiz.quiz_id}`}
                              className="w-full px-4 py-2 rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                              placeholder="e.g. en, tr, fr"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 pt-1">
                          <button
                            onClick={() => {
                              const name = document.getElementById(`name-${quiz.quiz_id}`).value;
                              const description = document.getElementById(`description-${quiz.quiz_id}`).value;
                              const image = document.getElementById(`image-${quiz.quiz_id}`).value;
                              const price = document.getElementById(`price-${quiz.quiz_id}`).value;
                              const language = document.getElementById(`language-${quiz.quiz_id}`).value;
                              handleUpdateQuiz(quiz.quiz_id, {
                                quiz_name: name,
                                quiz_description: description,
                                quiz_image: image,
                                quiz_price: parseFloat(price),
                                language: language
                              });
                            }}
                            className="px-4 py-2 rounded-lg bg-green-500 text-white font-medium hover:bg-green-600 transition-colors"
                          >
                            {t('admin.save')}
                          </button>
                          <button
                            onClick={() => setEditingQuiz(null)}
                            className="px-4 py-2 rounded-lg bg-slate-200 text-slate-700 font-medium hover:bg-slate-300 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-bold text-slate-900">{quiz.quiz_name}</h3>
                            {quiz.language && (
                              <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold uppercase tracking-wide">
                                {quiz.language}
                              </span>
                            )}
                            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">
                              {questionCounts[quiz.quiz_id] !== undefined
                                ? `${questionCounts[quiz.quiz_id]} questions`
                                : '...'}
                            </span>
                          </div>
                          {quiz.quiz_description && (
                            <p className="text-sm text-slate-500 line-clamp-2 mb-1">{quiz.quiz_description}</p>
                          )}
                          <p className="text-sm font-semibold text-emerald-600">${quiz.quiz_price}</p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                          <button
                            onClick={() => setEditingQuiz(quiz.quiz_id)}
                            className="p-2 rounded-lg bg-violet-100 text-violet-600 hover:bg-violet-200 transition-colors"
                            data-testid={`edit-quiz-${quiz.quiz_id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteQuiz(quiz.quiz_id)}
                            className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                            data-testid={`delete-quiz-${quiz.quiz_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Affiliates Tab */}
        {activeTab === 'affiliates' && (
          <div className="bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/50 shadow-lg">
            {selectedAffiliate ? (
              /* ── Affiliate detail view ── */
              <div>
                <button
                  onClick={() => setSelectedAffiliate(null)}
                  className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors mb-6 font-medium"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to Affiliates
                </button>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">{selectedAffiliate.email}</h2>
                    <p className="text-sm font-mono text-slate-500 mt-0.5">{selectedAffiliate.affiliate_id}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {editingAffiliateBalance === selectedAffiliate.affiliate_id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newBalanceValue}
                          onChange={(e) => setNewBalanceValue(e.target.value)}
                          className="w-28 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                          placeholder="0.00"
                        />
                        <button onClick={() => handleUpdateAffiliateBalance(selectedAffiliate.affiliate_id)} className="px-3 py-2 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 transition-colors">Save</button>
                        <button onClick={() => { setEditingAffiliateBalance(null); setNewBalanceValue(''); }} className="px-3 py-2 rounded-lg bg-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-300 transition-colors">Cancel</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setEditingAffiliateBalance(selectedAffiliate.affiliate_id); setNewBalanceValue(selectedAffiliate.balance?.toFixed(2) || '0.00'); }}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-100 text-emerald-700 font-semibold hover:bg-emerald-200 transition-colors text-sm"
                      >
                        <DollarSign className="w-4 h-4" />
                        Balance: ${(selectedAffiliate.balance || 0).toFixed(2)}
                        <Edit2 className="w-3 h-3 ml-1" />
                      </button>
                    )}
                    <button onClick={() => handleDeleteAffiliate(selectedAffiliate.affiliate_id)} className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <h3 className="text-lg font-bold text-slate-800 mb-3">Referred Quizzes ({affiliateReferrals.length})</h3>
                {affiliateReferralsLoading ? (
                  <div className="py-12 text-center text-slate-400">Loading referrals…</div>
                ) : affiliateReferrals.length === 0 ? (
                  <div className="py-12 text-center text-slate-400">No referrals yet.</div>
                ) : (
                  <div className="overflow-x-auto rounded-2xl border border-slate-100">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-slate-500 font-semibold text-xs uppercase tracking-wide">
                          <th className="px-4 py-3 text-left">Quiz</th>
                          <th className="px-4 py-3 text-left">Result</th>
                          <th className="px-4 py-3 text-left">Email</th>
                          <th className="px-4 py-3 text-center">Paid</th>
                          <th className="px-4 py-3 text-right">Commission</th>
                          <th className="px-4 py-3 text-left">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {affiliateReferrals.map((r) => (
                          <tr key={r.uid} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3 font-medium text-slate-800 max-w-[160px] truncate">{r.quiz_name}</td>
                            <td className="px-4 py-3 text-slate-600 max-w-[120px] truncate">{r.result_name}</td>
                            <td className="px-4 py-3 text-slate-600 max-w-[160px] truncate">{r.user_email || '—'}</td>
                            <td className="px-4 py-3 text-center">
                              {r.results_unlocked ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                                  <CheckCircle2 className="w-3 h-3" /> Paid
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs font-semibold">
                                  <AlertCircle className="w-3 h-3" /> Unpaid
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-emerald-600">
                              {r.results_unlocked ? `$${((quizzes.find(q => q.quiz_id === r.quiz_id)?.quiz_price || 0) * 0.2).toFixed(2)}` : '—'}
                            </td>
                            <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                              {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              /* ── Affiliates list ── */
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">Affiliates</h2>
                  <div className="flex items-center gap-3">
                    {affiliates.some(a => a.pending_payout) && (
                      <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-100 text-red-600 text-xs font-semibold">
                        <Bell className="w-3 h-3" />
                        {affiliates.filter(a => a.pending_payout).length} payout request{affiliates.filter(a => a.pending_payout).length > 1 ? 's' : ''}
                      </span>
                    )}
                    <span className="px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-sm font-semibold">
                      {affiliates.length} total
                    </span>
                  </div>
                </div>

                {affiliates.length === 0 ? (
                  <div className="py-16 text-center text-slate-400">No affiliates yet.</div>
                ) : (
                  <div className="overflow-x-auto rounded-2xl border border-slate-100">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-slate-500 font-semibold text-xs uppercase tracking-wide">
                          <th className="px-4 py-3 text-left">Email</th>
                          <th className="px-4 py-3 text-left">Affiliate ID</th>
                          <th className="px-4 py-3 text-center">Tests</th>
                          <th className="px-4 py-3 text-center">Paid</th>
                          <th className="px-4 py-3 text-right">Balance</th>
                          <th className="px-4 py-3 text-left">Joined</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {affiliates.map((aff) => (
                          <tr
                            key={aff.affiliate_id}
                            className="hover:bg-slate-50 transition-colors cursor-pointer"
                            onClick={() => handleSelectAffiliate(aff)}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-800">{aff.email}</span>
                                {aff.pending_payout && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setPayoutModalAffiliate(aff); }}
                                    className="relative flex-shrink-0 flex h-5 w-5 items-center justify-center"
                                    title="Pending payout request"
                                  >
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 items-center justify-center">
                                      <DollarSign className="w-2.5 h-2.5 text-white" />
                                    </span>
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 font-mono text-slate-500 text-xs tracking-widest">{aff.affiliate_id}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-violet-100 text-violet-700 text-xs font-bold">
                                {aff.total_tests ?? 0}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                                {aff.total_paid ?? 0}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              {editingAffiliateBalance === aff.affiliate_id ? (
                                <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={newBalanceValue}
                                    onChange={(e) => setNewBalanceValue(e.target.value)}
                                    className="w-24 px-2 py-1 rounded-lg border border-slate-200 bg-slate-50 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                                    placeholder="0.00"
                                  />
                                  <button onClick={() => handleUpdateAffiliateBalance(aff.affiliate_id)} className="px-2 py-1 rounded bg-emerald-500 text-white text-xs font-medium hover:bg-emerald-600">Save</button>
                                  <button onClick={() => { setEditingAffiliateBalance(null); setNewBalanceValue(''); }} className="px-2 py-1 rounded bg-slate-200 text-slate-700 text-xs hover:bg-slate-300">✕</button>
                                </div>
                              ) : (
                                <span className="font-semibold text-emerald-600">${(aff.balance || 0).toFixed(2)}</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                              {aff.created_at ? new Date(aff.created_at).toLocaleDateString() : '—'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                <button
                                  onClick={() => { setEditingAffiliateBalance(aff.affiliate_id); setNewBalanceValue(aff.balance?.toFixed(2) || '0.00'); }}
                                  className="p-1.5 rounded-lg bg-violet-100 text-violet-600 hover:bg-violet-200 transition-colors"
                                  title="Edit balance"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteAffiliate(aff.affiliate_id)}
                                  className="p-1.5 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                                  title="Delete affiliate"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Payout Request Modal */}
      {payoutModalAffiliate && payoutModalAffiliate.pending_payout && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-slate-900">Payout Request</h3>
              <button onClick={() => setPayoutModalAffiliate(null)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-slate-50 rounded-2xl p-4">
                <p className="text-xs text-slate-500 font-medium mb-0.5">Affiliate</p>
                <p className="font-semibold text-slate-900">{payoutModalAffiliate.email}</p>
                <p className="text-xs font-mono text-slate-400 mt-0.5">{payoutModalAffiliate.affiliate_id}</p>
              </div>
              <div className="bg-emerald-50 rounded-2xl p-4">
                <p className="text-xs text-slate-500 font-medium mb-0.5">Requested amount</p>
                <p className="text-3xl font-bold text-emerald-600">${(payoutModalAffiliate.pending_payout.amount || 0).toFixed(2)}</p>
              </div>
              <div className="bg-blue-50 rounded-2xl p-4">
                <p className="text-xs text-slate-500 font-medium mb-0.5">PayPal email to send payment to</p>
                <p className="font-bold text-blue-800 text-lg break-all">{payoutModalAffiliate.pending_payout.paypal_email}</p>
              </div>
              <div className="bg-slate-50 rounded-2xl px-4 py-2">
                <p className="text-xs text-slate-400">Requested on {payoutModalAffiliate.pending_payout.created_at ? new Date(payoutModalAffiliate.pending_payout.created_at).toLocaleString() : '—'}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setPayoutModalAffiliate(null)}
                className="flex-1 px-6 py-3 rounded-full font-bold text-slate-700 bg-slate-200 hover:bg-slate-300 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => handleMarkPayoutSeen(payoutModalAffiliate.affiliate_id)}
                className="flex-1 px-6 py-3 rounded-full font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 transition-all shadow-lg"
              >
                Mark as Reviewed ✓
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lock/Unlock Confirmation Modal */}
      {showLockModal && selectedResult && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                selectedResult.results_unlocked
                  ? 'bg-red-100'
                  : 'bg-green-100'
              }`}>
                {selectedResult.results_unlocked ? (
                  <Lock className="w-8 h-8 text-red-600" />
                ) : (
                  <Unlock className="w-8 h-8 text-green-600" />
                )}
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">
                {selectedResult.results_unlocked ? 'Lock Result?' : 'Unlock Result?'}
              </h3>
              <p className="text-slate-600">
                Are you sure you want to {selectedResult.results_unlocked ? 'lock' : 'unlock'} this result?
              </p>
              <div className="mt-4 p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-slate-700">
                  <span className="font-semibold">Quiz:</span> {selectedResult.quiz_name}
                </p>
                <p className="text-sm text-slate-700">
                  <span className="font-semibold">Result:</span> {selectedResult.result_name}
                </p>
                <p className="text-sm text-slate-700 font-mono">
                  <span className="font-semibold">UID:</span> {selectedResult.uid.slice(0, 8)}...
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowLockModal(false);
                  setSelectedResult(null);
                }}
                className="flex-1 px-6 py-3 rounded-full font-bold text-slate-700 bg-slate-200 hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmToggleLock}
                className={`flex-1 px-6 py-3 rounded-full font-bold text-white transition-all hover:scale-105 ${
                  selectedResult.results_unlocked
                    ? 'bg-gradient-to-r from-red-500 to-rose-500'
                    : 'bg-gradient-to-r from-green-500 to-emerald-500'
                }`}
              >
                {selectedResult.results_unlocked ? 'Lock' : 'Unlock'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Report Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-slate-900">Download Report</h3>
              <button
                onClick={() => {
                  setShowDownloadModal(false);
                  setDownloadStartDate('');
                  setDownloadEndDate('');
                }}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <p className="text-slate-600 mb-6">
              Select a date range to download results as a CSV file. Leave blank to download all results.
            </p>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={downloadStartDate}
                  onChange={(e) => setDownloadStartDate(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border-slate-200 bg-slate-50 focus:ring-2 focus:ring-violet-500/20"
                  data-testid="download-start-date"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={downloadEndDate}
                  onChange={(e) => setDownloadEndDate(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border-slate-200 bg-slate-50 focus:ring-2 focus:ring-violet-500/20"
                  data-testid="download-end-date"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDownloadModal(false);
                  setDownloadStartDate('');
                  setDownloadEndDate('');
                }}
                className="flex-1 px-6 py-3 rounded-full font-bold text-slate-700 bg-slate-200 hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDownloadReport}
                className="flex-1 px-6 py-3 rounded-full font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:scale-105 transition-all shadow-lg"
                data-testid="confirm-download"
              >
                Download CSV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}